import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/servicesTabStyle';
import { translate } from 'components/Translation';
import MainExpansionPanel from './subcomponents/MainExpansionPanel';
import Summary from './subcomponents/Summary';
import { ISelectedPackage } from 'views/PrepaidLoyalty/AddNewServices';
import { BodyMBold } from 'components/Typography/Typography';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import classNames from 'classnames';

// @ts-ignore
const useStyles = makeStyles(styles);

type IServicesTabProps = {
	selectedPackages: ISelectedPackage[];
	handleSelected: (singlePackage: ISelectedPackage) => void;
	numberOfLines: number;
	value: number;
	masterNumber: string;
	accountDetails: string;
	accountNumber: string;
	creditLimit: string;
	setCreditLimit: React.Dispatch<React.SetStateAction<string>>;
	userData: CorpUserData | undefined;
	setActiveTab: React.Dispatch<React.SetStateAction<number>>;
	paymentMethod: number;
	groupName?: string;
};

interface SingleService {
	title: string;
	items: {
		type: types.ecommerce.PackageType;
		groupBy?: types.topUpAddOns.IGroupBy;
	}[];
}

const services: SingleService[] = [
	{
		title: translate('prepaid.loyalty.add.new.services.proposed.packages'),
		items: [{ type: 'CORP_LOYALTY_PREPAID_MARHABA' }, { type: 'CORP_LOYALTY_PREPAID_SOCIAL' }],
	},
	{
		title: translate('prepaid.loyalty.add.new.services.mobile.internet'),
		items: [{ type: 'CORP_LOYALTY_PREPAID_DPS' }],
	},
	{
		title: translate('prepaid.loyalty.add.new.services.international.minutes'),
		items: [{ type: 'CORP_LOYALTY_PREPAID_INTER_MIN' }],
	},
	{
		title: translate('prepaid.loyalty.add.new.services.national.minutes'),
		items: [{ type: 'CORP_LOYALTY_PREPAID_NAT_MIN' }],
	},
	{
		title: translate('prepaid.loyalty.add.new.services.roaming.bundles'),
		items: [
			{
				type: 'CORP_LOYALTY_PREPAID_ROAMING',
				groupBy: {
					key: 'roamingType',
					values: ['JAWAZAK GCC', 'JAWAZAK WORLD', 'OTHER'],
				},
			},
		],
	},
	{
		title: translate('prepaid.loyalty.add.new.services.other'),
		items: [{ type: 'CORP_LOYALTY_PREPAID_OTHER' }],
	},
];

const ServicesTab = ({
	selectedPackages,
	handleSelected,
	numberOfLines,
	value,
	masterNumber,
	accountDetails,
	accountNumber,
	creditLimit,
	setCreditLimit,
	userData,
	setActiveTab,
	paymentMethod,
	groupName,
}: IServicesTabProps) => {
	const classes = useStyles();
	const [collapsedMain, setCollapsedMain] = useState<string>('');
	const [collapsedSub, setCollapsedSub] = useState<string>('');
	const [packagesLoading, setPackagesLoading] = useState<{ type: types.ecommerce.PackageType; loading: boolean }[]>(
		services
			.map(({ items }) => items)
			.flat()
			.map(({ type }) => ({ type, loading: false }))
	);
	const loading: boolean = packagesLoading.some(({ loading }) => loading);

	const handleCollapsedMain = (name: string) => {
		if (collapsedMain === name) {
			setCollapsedMain('');
			setCollapsedSub('');
		} else {
			setCollapsedMain(name);
			setCollapsedSub('');
		}
	};

	const handleCollapsedSub = (name: string) => {
		if (collapsedSub === name) {
			setCollapsedSub('');
		} else {
			setCollapsedSub(name);
		}
	};

	return (
		<div className={classes.servicesTab}>
			<div className={classes.servicesTitleWrapper}>
				<BodyMBold disableMargin>
					{translate('prepaid.loyalty.add.new.services.services.select.one.option')}
				</BodyMBold>
			</div>
			<ConditionalRender
				show={loading}
				onTrue={() => (
					<CustomCircularProgress text={translate('loading.it.can.take.a.while')} loading transparent />
				)}
			/>
			<div className={classNames({ [classes.mainExtensionPanelWrapper]: loading })}>
				{services.map((singleService, index) => (
					<MainExpansionPanel
						key={index}
						title={singleService.title}
						selectedPackages={selectedPackages}
						collapsedMain={collapsedMain}
						handleCollapsedMain={handleCollapsedMain}
						collapsedSub={collapsedSub}
						handleCollapsedSub={handleCollapsedSub}
						handleSelected={handleSelected}
						servicePackages={singleService.items}
						setPackagesLoading={setPackagesLoading}
					/>
				))}
			</div>
			<Summary
				numberOfLines={numberOfLines}
				selectedPackages={selectedPackages}
				value={value}
				masterNumber={masterNumber}
				accountDetails={accountDetails}
				accountNumber={accountNumber}
				creditLimit={creditLimit}
				setCreditLimit={setCreditLimit}
				userData={userData}
				setActiveTab={setActiveTab}
				paymentMethod={paymentMethod}
				groupName={groupName}
			/>
		</div>
	);
};
export default ServicesTab;
